import { useId, useState, type KeyboardEvent } from "react";
import { Input, Label, Text, TextField } from "react-aria-components";
import { Symbol } from "./Symbol";

/**
 * Formularfelder (design/README §9.12; Referenz `feld-text`, `k--radio`, `k--schalter`, `filter`). Die Beschriftung ist
 * der Feldname des Befehls (oberflaeche.md §1.2) und zugleich der zugängliche Name. Auswahl, Ankreuzfeld und Schalter
 * sind Kacheln mit Rolle `radio`, `checkbox` bzw. `switch`, mit einem Fingertipp auf die ganze sichtbare Fläche
 * bedienbar; gewählt ist eine 4-px-Kante mit Haken in eigener Spalte (kein schwarzer Grund, O-03).
 */
export function Textfeld({ label, wert, onChange, hilfe, typ = "text", autoComplete, eingabemodus, mono, autoFocus }: {
  label: string; wert: string; onChange: (w: string) => void; hilfe?: string; typ?: "text" | "tel" | "email" | "number" | "date" | "time";
  autoComplete?: string; eingabemodus?: "text" | "numeric" | "tel" | "email"; mono?: boolean; autoFocus?: boolean;
}) {
  return (
    <TextField className={`feld-text${hilfe ? " feld-text--hilfe" : ""}`} value={wert} onChange={onChange} type={typ} autoComplete={autoComplete} autoFocus={autoFocus}>
      <Label className="feld-text__b">{label}</Label>
      <Input type={typ} className={mono ? "mono" : undefined} inputMode={eingabemodus} />
      {hilfe ? <Text slot="description" className="feld-text__hilfe">{hilfe}</Text> : null}
    </TextField>
  );
}

function pfeiltasten(e: KeyboardEvent<HTMLDivElement>) {
  const knoepfe = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>("[role=radio]"));
  const i = knoepfe.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) return;
  const schritt = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
  if (!schritt) return;
  e.preventDefault();
  const ziel = knoepfe[(i + schritt + knoepfe.length) % knoepfe.length]!;
  ziel.focus();
  ziel.click();
}

/** Haken in eigener Spalte einer Auswahlkachel (Referenz `k__wahl`). */
function Wahl() {
  return <span className="k__wahl" aria-hidden="true"><Symbol name="haken" /></span>;
}

/**
 * Auswahl aus festen Werten (`radiogroup` mit `radio`): mit Symbolen als Querkacheln untereinander (Darstellung,
 * Ergebnis), ohne Symbole als Raster kurzer Werte (Filter, Anrede; Referenz `filter`).
 */
export function Auswahl({ label, optionen, wert, onChange, symbole }: {
  label: string; optionen: readonly string[]; wert: string | null; onChange: (w: string) => void; symbole?: Record<string, string>;
}) {
  const id = useId();
  const mitSymbolen = !!symbole && optionen.every((o) => symbole[o]);
  return (
    <div className="text-bereich auswahl">
      <span className="kopf" id={id}>{label}</span>
      <div className={mitSymbolen ? "auswahl__liste" : "filter"} role="radiogroup" aria-labelledby={id} onKeyDown={pfeiltasten} data-gleich="">
        {optionen.map((o, i) => (
          <button key={o} type="button" role="radio" className={mitSymbolen ? "k k--quer k--radio" : "k k--planung k--radio"} aria-checked={wert === o}
            aria-labelledby={`${id}-${i}`} tabIndex={wert === o || (wert === null && i === 0) ? 0 : -1} onClick={() => onChange(o)}>
            {mitSymbolen ? <Symbol name={symbole![o]!} /> : null}
            <span className="k__b" id={`${id}-${i}`}>{o}</span>
            {mitSymbolen ? <Wahl /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Ja/nein-Feld bzw. Menge (`checkbox`, oberflaeche.md §1.2): Querkachel mit Haken in eigener Spalte. */
export function Ankreuzfeld({ label, an, onChange, symbol }: { label: string; an: boolean; onChange: (an: boolean) => void; symbol?: string }) {
  const id = useId();
  return (
    <button type="button" role="checkbox" aria-checked={an} aria-labelledby={id} className={`k k--quer k--radio${symbol ? "" : " k--ohne-symbol"}`} onClick={() => onChange(!an)}>
      {symbol ? <Symbol name={symbol} /> : null}
      <span className="k__b" id={id}>{label}</span>
      <Wahl />
    </button>
  );
}

/** Ein/aus mit sofortiger Wirkung (`switch`): Querkachel mit Symbol, Begriff und Schalter (Referenz `k--schalter`). */
export function Schalter({ label, an, onChange, symbol = "haken" }: { label: string; an: boolean; onChange: (an: boolean) => void; symbol?: string }) {
  const id = useId();
  return (
    <button type="button" role="switch" aria-checked={an} aria-labelledby={id} className="k k--quer k--schalter" onClick={() => onChange(!an)}>
      <Symbol name={symbol} />
      <span className="k__b" id={id}>{label}</span>
      <span className="schalter" aria-hidden="true" />
    </button>
  );
}

/**
 * Auswahl aus einer längeren Liste (`combobox` mit `listbox`, oberflaeche.md §6 Einteilung): ein Fingertipp öffnet die
 * Liste, ein zweiter wählt. Name = Beschriftung (`aria-labelledby`), der gewählte Wert steht sichtbar im Feld.
 */
export function Klappliste({ label, optionen, wert, onChange, leer = "—" }: {
  label: string; optionen: ReadonlyArray<{ wert: string; text: string }>; wert: string | null; onChange: (w: string) => void; leer?: string;
}) {
  const id = useId();
  const [offen, setOffen] = useState(false);
  const gewaehlt = optionen.find((o) => o.wert === wert);
  return (
    <div className="klappliste">
      <button type="button" role="combobox" className="feld-text klappliste__feld" aria-labelledby={`${id}-n`} aria-expanded={offen}
        aria-controls={`${id}-l`} aria-haspopup="listbox" onClick={() => setOffen(!offen)}
        onKeyDown={(e) => { if (e.key === "Escape") setOffen(false); }}>
        <span className="feld-text__b" id={`${id}-n`}>{label}</span>
        <span className="klappliste__wert">{gewaehlt?.text ?? leer}</span>
        <Symbol name="weiter" />
      </button>
      {offen ? (
        <div className="klappliste__liste platte" role="listbox" id={`${id}-l`} aria-labelledby={`${id}-n`}>
          {optionen.map((o) => (
            <div key={o.wert} role="option" tabIndex={0} aria-selected={o.wert === wert} className="klappliste__option"
              onClick={() => { setOffen(false); if (o.wert !== wert) onChange(o.wert); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOffen(false); if (o.wert !== wert) onChange(o.wert); } }}>
              {o.text}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
